import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { dateHeureFr } from '../lib/format';
import { T } from '../i18n';

interface Note {
  _id: string; titre: string; texte: string; epinglee: boolean;
  createdAt: string; updatedAt: string;
  creePar?: { nom: string };
}

/**
 * Le carnet du patron.
 *
 * Hichem ecrit, Reda lit. Ce n'est ni une tache ni un dossier : c'est ce qu'on
 * ne veut pas oublier — un prix a renegocier, un client a rappeler, une voiture
 * a vendre. Contrairement au reste du logiciel, une note se modifie et se
 * supprime : elle n'engage personne et ne prouve rien.
 */
export default function Notes() {
  const { peut } = useAuth();
  const ecrit = peut('gerer.notes');
  const [liste, setListe] = useState<Note[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [edite, setEdite] = useState<Note | null>(null);
  const [nouvelle, setNouvelle] = useState(false);
  const [f, setF] = useState({ titre: '', texte: '' });
  const [envoi, setEnvoi] = useState(false);
  const [aSupprimer, setASupprimer] = useState<Note | null>(null);

  const charger = () => api.get<Note[]>('/notes').then(setListe)
    .catch((e) => setErreur(e instanceof ApiError ? e.erreur : T.notes.erreurChargement));
  useEffect(() => { void charger(); }, []);

  const ouvrir = (n: Note | null) => {
    setF(n ? { titre: n.titre, texte: n.texte } : { titre: '', texte: '' });
    if (n) setEdite(n); else setNouvelle(true);
  };
  const fermer = () => { setEdite(null); setNouvelle(false); setEnvoi(false); };

  async function enregistrer() {
    setEnvoi(true);
    try {
      if (edite) await api.patch(`/notes/${edite._id}`, { titre: f.titre.trim(), texte: f.texte });
      else await api.post('/notes', { titre: f.titre.trim(), texte: f.texte });
      fermer();
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.erreur : T.notes.erreurEnregistrement);
      setEnvoi(false);
    }
  }

  async function epingler(n: Note) {
    await api.patch(`/notes/${n._id}`, { epinglee: !n.epinglee });
    await charger();
  }

  async function supprimer(n: Note) {
    await api.del(`/notes/${n._id}`);
    setASupprimer(null);
    await charger();
  }

  if (erreur && !liste) return <Bandeau type="ko" titre={T.notes.neSAffichentPas}>{erreur}</Bandeau>;
  if (!liste) return <Squelette lignes={5} />;

  const tn = T.notes;
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{tn.titre}</h1>
          <p>
            {tn.compte(liste.length)}
            {ecrit ? tn.vousEcrivez : tn.vousLisez}
          </p>
        </div>
        {ecrit && (
          <div className="actions">
            <button className="btn btn-p" onClick={() => ouvrir(null)}>{tn.ecrire}</button>
          </div>
        )}
      </div>

      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      {liste.length === 0 ? (
        <Carte>
          <Vide titre={ecrit ? tn.rienNote : tn.responsableRien} />
        </Carte>
      ) : (
        <div className="grille g2" style={{ alignItems: 'start' }}>
          {liste.map((n) => (
            <Carte key={n._id} titre={n.titre} action={
              ecrit ? (
                <div className="actions">
                  <button className={`btn btn-s ${n.epinglee ? 'btn-p' : ''}`}
                          onClick={() => void epingler(n)}
                          title={n.epinglee ? tn.nePlusEpingler : tn.epinglerEnHaut}>
                    {n.epinglee ? tn.epinglee : tn.epingler}
                  </button>
                  <button className="btn btn-s" onClick={() => ouvrir(n)}>{tn.modifier}</button>
                  <button className="btn btn-s" onClick={() => setASupprimer(n)}>{tn.supprimer}</button>
                </div>
              ) : n.epinglee ? <span className="badge en_cours">{tn.epinglee}</span> : undefined
            }>
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{n.texte || <span className="sourdine">{tn.vide}</span>}</p>
              <p className="mini sourdine" style={{ marginTop: 12, marginBottom: 0 }}>
                {n.creePar?.nom ?? '—'} · {tn.modifieeLe(dateHeureFr(n.updatedAt))}
              </p>
            </Carte>
          ))}
        </div>
      )}

      {(edite || nouvelle) && (
        <Modale titre={edite ? tn.modifierNote : tn.ecrire} onFermer={fermer}>
          <Champ label={tn.titreChamp}>
            <input value={f.titre} maxLength={140} autoFocus
                   onChange={(e) => setF({ ...f, titre: e.target.value })}
                   placeholder={tn.titreExemple} />
          </Champ>
          <div style={{ marginTop: 12 }}>
            <Champ label={tn.note}>
              <textarea rows={8} value={f.texte} maxLength={8000}
                        onChange={(e) => setF({ ...f, texte: e.target.value })}
                        placeholder={tn.noteExemple} />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={fermer}>{tn.annuler}</button>
            <button className="btn btn-p" disabled={envoi || !f.titre.trim()} onClick={() => void enregistrer()}>
              {tn.enregistrer}
            </button>
          </div>
        </Modale>
      )}

      {aSupprimer && (
        <Modale titre={tn.supprimerTitre(aSupprimer.titre)} onFermer={() => setASupprimer(null)}>
          <p className="mini sourdine">
            {tn.supprimerTexte}
          </p>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setASupprimer(null)}>{tn.annuler}</button>
            <button className="btn btn-d" onClick={() => void supprimer(aSupprimer)}>{tn.supprimer}</button>
          </div>
        </Modale>
      )}
    </>
  );
}
